<template>
  <WdsStack direction-row justify-space-between>
    <div>
      <WdsHeading type="title2">{{ $t('Talent Channels') }}</WdsHeading>
      <WdsStack direction-row align-center gap-sm>
        <WdsText secondary>
          <template v-if="!isPublished">
            {{ $t('Add or invite talent to this job') }}
          </template>
          <template v-else>
            {{ $t('Shared with') }}
            <span v-if="!viewerCanShare">
              {{
                $tc(':count Staffing Agency | :count Staffing Agencies', countStaffingAgencyShares)
              }}
            </span>
            <WdsButtonText v-else @click="toggleContactsModal(true)">
              {{
                $tc(':count Staffing Agency | :count Staffing Agencies', countStaffingAgencyShares)
              }}
            </WdsButtonText>
          </template>
        </WdsText>
        <div class="tw-text-gray-700">&bull;</div>
        <WdsText secondary>
          {{
            isForMarketplace ? $t('Published to Marketplace') : $t('Not published to Marketplace')
          }}
        </WdsText>
      </WdsStack>
    </div>

    <WdsMenu z-index="21" placement="bottom-end">
      <template #trigger>
        <WdsButton :disabled="isPublished && !isAvailable" secondary>
          {{ $t('Add/Invite Talent') }}
        </WdsButton>
      </template>
      <WdsMenuOption @click="toggleShortlistingContactsSelectionModal(true)">
        {{ $t('Add contact to Private Shortlist') }}
      </WdsMenuOption>

      <WdsMenuDivider />

      <WdsMenuOption
        v-if="!isPublished"
        theme="WARNING"
        :href="
          laravelNamedRoute(namedRoutes.yjob.edit, {
            yjob: props.jobId,
          })
        "
      >
        {{ $t('Make this job available') }}
      </WdsMenuOption>

      <WdsMenuOption
        :disabled="!isPublished"
        @click="toggleInviteToApplyContactsSelectionModal(true)"
      >
        {{ $t('Share with Trusted Contacts') }}
      </WdsMenuOption>

      <WdsMenuOption :disabled="!isPublished" @click="toggleContactsModal(true)">
        {{ $t('Share with Staffing Agencies') }}
      </WdsMenuOption>

      <WdsMenuOption :disabled="!isPublished" @click="toggleMarketplaceConfirmationModal">
        {{ !isForMarketplace ? $t('Post job to Marketplace') : $t('Remove job from Marketplace') }}
      </WdsMenuOption>
    </WdsMenu>
  </WdsStack>

  <teleport to="body">
    <WdsModal
      lg
      :show="showShortlistingContactsSelectionModal"
      :title="$t('Shortlist workers')"
      data-test="candidates-shortlist-modal"
      @cancel="toggleShortlistingContactsSelectionModal(false)"
    >
      <WdsSpinner v-if="loadingJobCandidates" indeterminate />
      <SelectShortlistingCandidatesModal
        v-else
        :company-id="companyId"
        :job-id="jobId"
        :pre-selected-candidates="preSelectedShortlistingCandidates"
        @candidates-updated="handleContactsUpdated"
        @cancel="toggleShortlistingContactsSelectionModal(false)"
      />
    </WdsModal>

    <WdsModal
      lg
      :show="showInviteToApplyContactsSelectionModal"
      :title="$t('Invite to apply')"
      data-test="candidates-invite-to-apply-modal"
      @cancel="toggleInviteToApplyContactsSelectionModal(false)"
    >
      <WdsSpinner v-if="loadingJobCandidates" indeterminate />
      <SelectInviteToApplyCandidatesModal
        v-else
        :company-id="companyId"
        :job-id="jobId"
        :pre-selected-candidates="preSelectedInvitedToApplyCandidates"
        @candidates-updated="handleContactsUpdated"
        @cancel="toggleInviteToApplyContactsSelectionModal(false)"
      />
    </WdsModal>

    <WdsModal
      lg
      :show="showContactsModal"
      :title="$t('Select recruiters')"
      data-test="collaborators-invite-modal"
      @cancel="toggleContactsModal(false)"
    >
      <WdsSpinner v-if="loadingJobShares" indeterminate />
      <RecruitersSelection
        v-else
        :job-id="jobId"
        :pre-selected-recruiters="state.contacts"
        @updated="handleContactsUpdated"
        @cancel="toggleContactsModal(false)"
      />
    </WdsModal>

    <WdsModal
      :title="marketplaceModal.title"
      :show="showMarketplaceConfirmationModal"
      data-test="marketplace-confirmation-modal"
      @cancel="marketplaceModalHandleCancel"
    >
      {{ marketplaceModal.body }}
      <template #footer>
        <WdsStack justify-end gap-sm align-center>
          <WdsSpinner v-show="updateJobLoading" indeterminate />
          <WdsButton
            tertiary
            :disabled="updateJobLoading"
            @click.stop.prevent="marketplaceModalHandleCancel"
          >
            {{ marketplaceModal.cancelButtonText }}
          </WdsButton>
          <WdsButton
            :primary="!isForMarketplace"
            :error="isForMarketplace"
            :disabled="updateJobLoading"
            data-test="marketplace-confirmation-button"
            @click="marketplaceModalHandleAction"
          >
            {{ marketplaceModal.confirmButtonText }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsHeading,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsMenu,
  WdsButton,
  WdsMenuOption,
  WdsMenuDivider,
  WdsButtonText,
} from '@worksome/wds-components'
import { ref, provide, readonly, computed } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'

import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import { type FragmentType, useFragment } from '@/gql'
import { JobVisibility } from '@/gql/graphql'
import AccountType from '@/utils/AccountType'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { NetworkError } from '@/utils/notification'

import {
  ManageWorkersInterface,
  manageWorkersKey,
} from '@/components/Contacts/CompanyRecruiter/injectionKeys'
import { Recruiter } from '@/components/Job/hiring-types'
import RecruitersSelection from '@/components/Job/RecruitersSelection.vue'
import {
  JobFormShareableRecruiterFragment,
  JobCandidateFragment,
} from '@/components/Job/graphql/fragments'
import { UpdateJobMutation } from '@/components/Job/graphql/mutations'

import { GetJobSharesQuery } from '@/components/Job/Share/graphql/queries'
import { GetJobCandidatesQuery } from '@/components/Job/Candidates/graphql/queries'

import SelectShortlistingCandidatesModal from '@/components/Job/Candidates/SelectShortlistingCandidatesModal.vue'
import SelectInviteToApplyCandidatesModal from '@/components/Job/Candidates/SelectInviteToApplyCandidatesModal.vue'

const props = withDefaults(
  defineProps<{
    companyId: string
    jobId: string
    isPublished?: boolean
    isAvailable?: boolean
    isForMarketplace?: boolean
    countStaffingAgencyShares: number
    viewerCanShare: boolean
  }>(),
  {
    isPublished: false,
    isAvailable: false,
    isForMarketplace: false,
    countStaffingAgencyShares: 0,
    viewerCanShare: false,
  },
)

const emit = defineEmits(['contacts-updated'])

const state = ref<{
  contacts: Recruiter[]
}>({
  contacts: [],
})

const manageWorkers = ref(false)
const showContactsModal = ref<boolean>(false)
const showShortlistingContactsSelectionModal = ref<boolean>(false)
const showInviteToApplyContactsSelectionModal = ref<boolean>(false)
const showMarketplaceConfirmationModal = ref<boolean>(false)

provide<ManageWorkersInterface>(manageWorkersKey, {
  manageWorkers: readonly(manageWorkers),
})

const {
  loading: loadingJobCandidates,
  refetch: refetchJobCandidates,
  result: jobCandidatesResult,
} = useQuery(
  GetJobCandidatesQuery,
  {
    jobIds: [props.jobId],
    first: PER_PAGE_MAXIMUM,
  },
  { clientId: 'publicApi' },
)

const {
  loading: loadingJobShares,
  refetch: refetchJobShares,
  onResult: onJobSharesResult,
} = useQuery(
  GetJobSharesQuery,
  {
    jobId: props.jobId,
  },
  { clientId: 'publicApi' },
)

const { loading: updateJobLoading, mutate: mutateUpdateJob } = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

onJobSharesResult((queryJobResult) => {
  if (!queryJobResult.loading && queryJobResult.data && queryJobResult.data.job) {
    const { job } = queryJobResult.data

    state.value.contacts = []
    manageWorkers.value = job.company?.settings?.staffingAgencyManagesWorker ?? false
    job.shares.data.forEach((share: any) => {
      if (share.account.__typename && AccountType.isRecruiter(share.account.__typename)) {
        const recruiter = useFragment(JobFormShareableRecruiterFragment, share!.account!)

        state.value.contacts.push({
          id: share.id,
          recruiterId: recruiter.id,
          avatar: recruiter.avatar,
          name: recruiter.name,
        } as Recruiter)
      }
    })
  }
})

const jobCandidates = computed(() =>
  jobCandidatesResult.value?.jobCandidates.data.map(
    (entry: FragmentType<typeof JobCandidateFragment>) => useFragment(JobCandidateFragment, entry),
  ),
)

const preSelectedShortlistingCandidates = computed(
  () => jobCandidates.value?.map((jobCandidate) => jobCandidate.worker.id) ?? [],
)

const preSelectedInvitedToApplyCandidates = computed(
  () =>
    jobCandidates.value
      ?.filter((jobCandidate) => jobCandidate.jobShare?.isActive)
      .map((jobCandidate) => jobCandidate.worker.id) ?? [],
)

const toggleShortlistingContactsSelectionModal = (show: boolean) => {
  showShortlistingContactsSelectionModal.value = show
}

const toggleInviteToApplyContactsSelectionModal = (show: boolean) => {
  showInviteToApplyContactsSelectionModal.value = show
}

const toggleContactsModal = (show: boolean) => {
  showContactsModal.value = show
}

const toggleMarketplaceConfirmationModal = () => {
  showMarketplaceConfirmationModal.value = !showMarketplaceConfirmationModal.value
}

const handleContactsUpdated = async () => {
  await refetchJobShares()

  await refetchJobCandidates()

  emit('contacts-updated')
}

const marketplaceModal = computed(() => ({
  title: props.isForMarketplace ? $t('Remove from marketplace') : $t('Add to marketplace'),
  body: props.isForMarketplace
    ? $t('Your job will no longer be listed publicly')
    : $t('Your job will be listed publicly'),
  cancelButtonText: props.isForMarketplace ? $t('Keep sharing') : $t('Keep private'),
  confirmButtonText: props.isForMarketplace ? $t('Remove access') : $t('Make public'),
}))

const marketplaceModalHandleCancel = () => {
  showMarketplaceConfirmationModal.value = false
}

const marketplaceModalHandleAction = async () => {
  try {
    await mutateUpdateJob({
      input: {
        id: props.jobId,
        visibility: props.isForMarketplace ? [] : [JobVisibility.VisibleForMarketplace],
      },
    })

    showMarketplaceConfirmationModal.value = false

    emit('contacts-updated')
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: $t('An error occurred while publishing your job.'),
    }).dispatch()

    throw ex
  }
}
</script>

<style lang="scss">
:deep(.wds-modal) {
  z-index: 19;
}

.scroll-to-link {
  @apply tw-text-gray-700;
  @apply tw-underline;
  @apply tw-cursor-pointer;
  &:hover {
    @apply tw-text-indigo-400;
    @apply tw-underline;
  }
}
</style>
